/**
 * Smart Nutrition Suggestions Hook
 * Sprint 3: AI-powered meal suggestions based on:
 * 1. Remaining macro budget for today
 * 2. Yesterday's meals per meal type ("same as yesterday")
 * 3. Most frequently logged foods that fit within remaining budget
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useDailySummary } from './use-nutrition-summary';
import type { MealType, NutritionLog } from '@/lib/nutrition';
import type { FoodSearchResult } from '@/lib/nutrition/food-search';
import { format, subDays } from 'date-fns';

export interface MacroBudget {
  calories: { remaining: number; target: number; consumed: number };
  protein_g: { remaining: number; target: number; consumed: number };
  carbs_g: { remaining: number; target: number; consumed: number };
  fat_g: { remaining: number; target: number; consumed: number };
}

export interface YesterdayMeal {
  meal_type: MealType;
  food_description: string;
  calories: number | null;
  protein_g: number | null;
  carbs_g: number | null;
  fat_g: number | null;
  log_id: string;
}

export interface SmartFoodSuggestion {
  food: FoodSearchResult;
  reason: 'frequent' | 'fits_budget' | 'low_calorie';
  useCount: number;
  fitsCalorieBudget: boolean;
}

export interface SmartSuggestionsResult {
  budget: MacroBudget | null;
  hasGoal: boolean;
  yesterdayMeals: YesterdayMeal[];
  quickLogFoods: SmartFoodSuggestion[];
  isLoading: boolean;
}

/**
 * Hook that provides smart nutrition suggestions for a client.
 */
export function useSmartSuggestions(clientId: string): SmartSuggestionsResult {
  const today = new Date();
  const yesterday = subDays(today, 1);
  const yesterdayStr = format(yesterday, 'yyyy-MM-dd');

  // Get today's summary + goal for budget calculation
  const { data: todaySummary, isLoading: summaryLoading } = useDailySummary({
    clientId,
    date: today,
  });

  // Get yesterday's meals for "same as yesterday"
  const { data: yesterdayLogs, isLoading: yesterdayLoading } = useQuery({
    queryKey: ['nutrition-yesterday', clientId, yesterdayStr],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('nutrition_logs')
        .select('id, meal_type, food_description, calories, protein_g, carbs_g, fat_g')
        .eq('client_id', clientId)
        .eq('logged_date', yesterdayStr)
        .neq('meal_type', 'water')
        .order('logged_at', { ascending: true });

      if (error) throw error;
      return data as Pick<NutritionLog, 'id' | 'meal_type' | 'food_description' | 'calories' | 'protein_g' | 'carbs_g' | 'fat_g'>[];
    },
    enabled: !!clientId,
    staleTime: 5 * 60 * 1000, // 5 min
  });

  // Get top frequent foods from recent_foods
  const { data: frequentFoods, isLoading: frequentLoading } = useQuery({
    queryKey: ['nutrition-frequent', clientId],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from('recent_foods')
        .select('*')
        .eq('user_id', user.id)
        .order('use_count', { ascending: false })
        .order('last_used_at', { ascending: false })
        .limit(12);

      // Degrade gracefully: "frequent foods" is a non-critical convenience
      // section. If the source is unavailable (e.g. the recent_foods table
      // isn't present in this environment), return [] instead of throwing —
      // a missing optional feature must never crash the nutrition dashboard.
      if (error) {
        console.warn('[useSmartSuggestions] frequent foods unavailable:', error);
        return [];
      }
      return data || [];
    },
    enabled: !!clientId,
    staleTime: 5 * 60 * 1000,
    retry: false, // don't hammer a missing table
  });

  // Compute budget
  const budget: MacroBudget | null = todaySummary?.goal ? {
    calories: {
      target: todaySummary.goal.target_calories ?? 0,
      consumed: todaySummary.total_calories,
      remaining: Math.max(0, (todaySummary.goal.target_calories ?? 0) - todaySummary.total_calories),
    },
    protein_g: {
      target: todaySummary.goal.target_protein_g ?? 0,
      consumed: todaySummary.total_protein_g,
      remaining: Math.max(0, (todaySummary.goal.target_protein_g ?? 0) - todaySummary.total_protein_g),
    },
    carbs_g: {
      target: todaySummary.goal.target_carbs_g ?? 0,
      consumed: todaySummary.total_carbs_g,
      remaining: Math.max(0, (todaySummary.goal.target_carbs_g ?? 0) - todaySummary.total_carbs_g),
    },
    fat_g: {
      target: todaySummary.goal.target_fat_g ?? 0,
      consumed: todaySummary.total_fat_g,
      remaining: Math.max(0, (todaySummary.goal.target_fat_g ?? 0) - todaySummary.total_fat_g),
    },
  } : null;

  // Map yesterday meals
  const yesterdayMeals: YesterdayMeal[] = (yesterdayLogs || [])
    .filter((log) => log.food_description)
    .map((log) => ({
      meal_type: log.meal_type as MealType,
      food_description: log.food_description!,
      calories: log.calories,
      protein_g: log.protein_g,
      carbs_g: log.carbs_g,
      fat_g: log.fat_g,
      log_id: log.id,
    }));

  // Build smart food suggestions from frequent foods.
  // Guard against null/malformed food_data (a NULL or non-object JSON column would
  // otherwise crash on `food.calories`). Skip any row without a usable food object.
  const quickLogFoods: SmartFoodSuggestion[] = (frequentFoods || [])
    .map((rf) => {
      const food = rf.food_data as unknown as FoodSearchResult | null;
      if (!food || typeof food !== 'object') return null;
      const fitsCalorieBudget = budget
        ? (food.calories ?? 0) <= budget.calories.remaining
        : true;

      return {
        food,
        reason: fitsCalorieBudget ? 'fits_budget' : 'frequent',
        useCount: rf.use_count,
        fitsCalorieBudget,
      } as SmartFoodSuggestion;
    })
    .filter((s): s is SmartFoodSuggestion => s !== null);

  // Sort: foods that fit budget first, then by use count
  quickLogFoods.sort((a, b) => {
    if (a.fitsCalorieBudget !== b.fitsCalorieBudget) {
      return a.fitsCalorieBudget ? -1 : 1;
    }
    return b.useCount - a.useCount;
  });

  return {
    budget,
    hasGoal: !!todaySummary?.goal,
    yesterdayMeals,
    quickLogFoods: quickLogFoods.slice(0, 8), // Top 8
    isLoading: summaryLoading || yesterdayLoading || frequentLoading,
  };
}
